const zoneList: string[] = [
  'UK',
  'NY', // "WA",
  'SG',
  'DE',
  'TX',
  'HK',
  'LA',
  'GA',
  'SYD',
  'BU',
  'JP',
  'FR',
  'KC',
  'MI',
  'MN',
  'AMS',
  'SIL',
  'BR',
  'ES',
  'IL',
  'NO',
  'RU',
  'IT',
  'JH',
  'IN',
  'CZ',
  'TR',
  'PL',
  'CA',
  'TH',
  'LT',
  'PER',
  'MEL',
  'AUC',
  'BRB',
  'ASB',
  'KR',
  'ADL',
  'AT',
  'FI',
  'ISR',
  'VA',
  'AE',
  'DEN',
  'SE',
  'CH',
  'ND',
  'NG',
  'CL',
  'BG',
  'PT',
  'DK',
  'BE',
  'IE',
  'PHX',
  'CEN',
  'GR', // "EG",
  'SK',
  'KE',
  'MX',
  'KZ',
  'AR',
  'CR',
  'UA',
  'CO',
  'PE',
  'EC',
  'RJ',
  'ID',
  'VN',
  'PH', // "MY",
  'PA',
  'FO',
  'LJ',
  'LU',
  'MS',
  'MD',
  'CY',
  'BA',
  'GEO',
  'LV',
  'HU',
  'AZ',
  'HOU',
  'RS',
  'PK',
  'IS',
  'TW',
  'IQ',
  'OG',
  'RI',
  'PR',
  'HI',
  'GT',
  'BS',
  'CT',
  'BO',
  'AM',
  'FU',
  'BHR',
  'PP',
  'NP',
  'MG',
  'BD',
  'HR',
  'CLT',
  'MSP', // "GU",
  'IQ2', // "SSA",
  'CWB',
  'LAP', // "MI2",
];

interface MetricsRegisterResponse {
  uniqueId: string;
}

/**
 * RUM (Real User Monitoring) job for tracking performance metrics
 * Implements a job pattern to manage zone latency testing and metrics collection
 */
export class RumJob {
  private zoneList: string[] = [...zoneList];
  private currentZoneIndex: number = 0;
  private isRunning: boolean = false;
  private timeoutId: number | null = null;

  /**
   * Shuffle an array using Fisher-Yates algorithm
   */
  private shuffle<T>(array: T[]): T[] {
    let currentIndex = array.length;
    let randomIndex: number;

    while (currentIndex !== 0) {
      randomIndex = Math.floor(Math.random() * currentIndex);
      currentIndex--;
      [array[currentIndex], array[randomIndex]] = [
        array[randomIndex],
        array[currentIndex],
      ];
    }

    return array;
  }

  /**
   * Run metrics test by registering and fetching test endpoint
   */
  private runMetricsTest(): void {
    // Only run if job is still active
    if (!this.isRunning) {
      return;
    }

    fetch('https://metrics-bunny.net/test/register')
      .then((response: Response) => {
        if (!response.ok) {
          throw new Error(`RUM registration failed: ${response.status}`);
        }
        return response.json();
      })
      .then((data: MetricsRegisterResponse) => {
        // Check again if job is still running before proceeding
        if (!this.isRunning) {
          return Promise.reject(new Error('RUM job stopped'));
        }
        const uniqueId = data.uniqueId;
        const url = `https://${uniqueId}.metrics-bunny.net/test/hello`;
        return fetch(url);
      })
      .then((response: Response) => {
        if (!response.ok) {
          throw new Error(`RUM test failed: ${response.status}`);
        }
        return response.text();
      })
      .then((result: string) => {
        console.log('RUM test response:', result);
      })
      .catch((error: unknown) => {
        console.error('RUM test error:', error);
      });
  }

  /**
   * Schedule the next zone latency test
   */
  private scheduleNextTest(): void {
    // Don't schedule if job has been stopped
    if (!this.isRunning) {
      return;
    }

    this.currentZoneIndex++;
    if (this.currentZoneIndex < this.zoneList.length) {
      this.timeoutId = setTimeout(() => this.runNextTest(), 500);
    }
  }

  /**
   * Run latency test for current zone by fetching the same resource twice
   */
  private runNextTest(): void {
    // Don't run if job has been stopped or index is out of bounds
    if (!this.isRunning || this.currentZoneIndex >= this.zoneList.length) {
      return;
    }

    const currentZoneCode = this.zoneList[this.currentZoneIndex].toLowerCase();
    const startTime = new Date().valueOf();
    const fetchUrl =
      `https://edgezone-${currentZoneCode}.bunnyinfra.net/500b.jpg?s=${startTime}`;

    fetch(fetchUrl)
      .then(() => {
        // Check again if job is still running before second fetch
        if (!this.isRunning) {
          return;
        }
        const secondFetchStartTime = new Date().valueOf();
        return fetch(fetchUrl).then(() => {
          const endTime = new Date().valueOf();
          const diff = endTime - secondFetchStartTime;
          // Track performance (fire and forget)
          fetch(
            `https://rum-metrics.bunny.net/trackperformance?zone=${currentZoneCode}&latency=${diff}`,
          ).catch(() => {
            // Silently ignore tracking errors
          });
          this.scheduleNextTest();
        });
      })
      .catch(() => {
        // Continue to next test on error
        this.scheduleNextTest();
      });
  }

  /**
   * Start the RUM job
   */
  public start(): void {
    if (this.isRunning) {
      return;
    }

    this.isRunning = true;
    this.currentZoneIndex = 0;
    this.shuffle(this.zoneList);
    this.runNextTest();
    this.runMetricsTest();
  }

  /**
   * Stop the RUM job
   */
  public stop(): void {
    this.isRunning = false;
    if (this.timeoutId !== null) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }

  /**
   * Check if the job is currently running
   */
  public get running(): boolean {
    return this.isRunning;
  }
}
